import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { api } from '../../services/api';
import { isProduction, INITIAL_DOMAIN_URL } from '../../../config/env';

/**
 * Admin: rotating attendance QR for a single training event.
 *
 * - Polls `api.getEventAttendanceQrToken(eventId)` on a recurring interval
 *   (server tells us how often via `refreshIntervalSeconds`; `<= 0` disables rotation).
 * - Renders the latest token as a QR code on a `<canvas>`.
 * - Shows a thin progress bar that ticks down between refreshes so the
 *   organiser knows the QR is live.
 * - If a refresh fails, we keep showing the last QR rather than blanking
 *   the screen — agents in the room mid-scan shouldn't be interrupted by
 *   a transient network blip.
 *
 * This dialog never writes attendance itself. It only issues tokens. The
 * agent's scanner opens the Super Picker deep link; the backend later
 * verifies the (still-valid) QR token and upserts the attendance row.
 */

interface EventAttendanceQrDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  eventName: string;
  /**
   * `'clockIn'` (default) issues the regular attendance QR — scanning it
   * records an `IsClockIn=1` row. `'clockOut'` issues a clock-out QR
   * whose scans insert an `IsClockIn=0` row instead. Only meaningful
   * for events in categories that support clock-out (C17041 / C17042).
   */
  mode?: 'clockIn' | 'clockOut';
  /**
   * When provided, this dialog polls the per-session QR endpoint instead
   * of the event-level endpoint. The token issued embeds the sessionId
   * claim so the scan endpoint stamps the attendance row with it.
   */
  sessionId?: number | null;
  /** Session name for the dialog subtitle when sessionId is set. */
  sessionName?: string | null;
  /**
   * When false/null, omit location-related attendee instructions.
   * Defaults to false (location not required).
   */
  locationRequired?: boolean | null;
}

// Size of the rendered QR canvas. Picked large enough to scan reliably from
// across a training room — most attendees will scan from 2-4m away with
// phone cameras of varying quality.
const QR_SIZE_PX = 520;

const EventAttendanceQrDialog: React.FC<EventAttendanceQrDialogProps> = ({
  open,
  onClose,
  eventId,
  eventName,
  mode = 'clockIn',
  sessionId = null,
  sessionName = null,
  locationRequired = false,
}) => {
  const isClockIn = mode === 'clockIn';
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenInfo, setTokenInfo] = useState<{
    token: string;
    expiresAt: string;
    refreshIntervalSeconds: number;
  } | null>(null);
  // Drives the visual countdown bar. Updated every 250ms while a token is live.
  const [progressPct, setProgressPct] = useState<number>(100);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const refreshToken = useCallback(async () => {
    if (sessionId != null) {
      if (!Number.isFinite(sessionId)) return;
    } else if (!eventId) {
      return;
    }
    setLoading(true);
    try {
      const res =
        sessionId != null
          ? await api.getSessionAttendanceQrToken(sessionId, { clockIn: isClockIn })
          : await api.getEventAttendanceQrToken(eventId, { clockIn: isClockIn });
      setTokenInfo({
        token: res.token,
        expiresAt: res.expiresAt,
        refreshIntervalSeconds: res.refreshIntervalSeconds,
      });
      setError(null);
    } catch (err) {
      // Keep the previous token visible (if any) on failure so a
      // mid-event network blip doesn't interrupt scans.
      setError(err instanceof Error ? err.message : 'Failed to refresh QR');
    } finally {
      setLoading(false);
    }
  }, [eventId, isClockIn, sessionId]);

  // Start / stop the polling loop with the dialog's open state.
  useEffect(() => {
    if (!open || !eventId) {
      setTokenInfo(null);
      setError(null);
      setProgressPct(100);
      setSecondsLeft(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    void refreshToken();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, eventId, refreshToken]);

  // Schedule the next token refresh based on the cadence the server told
  // us about. `refreshIntervalSeconds <= 0` means the QR should not rotate.
  useEffect(() => {
    if (!open || !tokenInfo) return;
    const refreshSec = tokenInfo.refreshIntervalSeconds;
    if (!Number.isFinite(refreshSec) || refreshSec <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    const refreshMs = Math.max(5, refreshSec) * 1000;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      void refreshToken();
    }, refreshMs);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, tokenInfo, refreshToken]);

  // Visual countdown bar. Decoupled from the actual refresh so the bar
  // stays smooth even when the network call takes time.
  useEffect(() => {
    if (!tokenInfo) {
      setProgressPct(100);
      setSecondsLeft(0);
      return;
    }
    const refreshSec = tokenInfo.refreshIntervalSeconds;
    if (!Number.isFinite(refreshSec) || refreshSec <= 0) {
      setProgressPct(100);
      setSecondsLeft(0);
      return;
    }
    const startedAt = Date.now();
    const tick = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const remaining = Math.max(0, refreshSec - elapsed);
      setSecondsLeft(Math.ceil(remaining));
      setProgressPct(Math.max(0, Math.min(100, (remaining / refreshSec) * 100)));
    }, 250);
    return () => clearInterval(tick);
  }, [tokenInfo]);

  // Re-render the QR whenever the token changes.
  // Production deep-links go through htns.sg so company SSO runs before
  // Super Picker marks attendance with the still-valid QR JWT.
  useEffect(() => {
    if (!tokenInfo || !canvasRef.current) return;
    const baseUrl = isProduction ? INITIAL_DOMAIN_URL : window.location.origin;
    const sessionParam =
      sessionId != null && Number.isFinite(sessionId) ? `&sessionId=${sessionId}` : '';
    const qrUrl = `${baseUrl}/super-picker?redirect=mark-attendance&eventId=${encodeURIComponent(eventId)}${sessionParam}&attendanceToken=${encodeURIComponent(tokenInfo.token)}`;
    QRCode.toCanvas(canvasRef.current, qrUrl, {
      width: QR_SIZE_PX,
      margin: 1,
      errorCorrectionLevel: 'M',
    }).catch((err) => {
      console.error('Failed to render attendance QR:', err);
    });
  }, [tokenInfo, eventId, sessionId]);

  // If the user backgrounds the tab and comes back, force a refresh —
  // the existing token may have expired. Skip when rotation is disabled.
  useEffect(() => {
    if (!open) return;
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      if (tokenInfo && tokenInfo.refreshIntervalSeconds <= 0) return;
      void refreshToken();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [open, refreshToken, tokenInfo]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: { xs: 'auto', sm: 720 } } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {!isClockIn ? 'Clock-Out QR' : 'Attendance QR'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {sessionName ? `${eventName} · ${sessionName}` : eventName}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh now">
            <span>
              <IconButton size="small" onClick={() => void refreshToken()} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Grid container spacing={3} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
          {/* Left Column: QR Code */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: QR_SIZE_PX,
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: '#fff',
                  p: 1.5,
                }}
              >
                {/* Canvas is rendered at QR_SIZE_PX intrinsic resolution for
                    crispness, then scaled to fit via CSS so it stays inside
                    the dialog on narrow viewports. */}
                <canvas
                  ref={canvasRef}
                  width={QR_SIZE_PX}
                  height={QR_SIZE_PX}
                  style={{ width: '100%', height: '100%', maxWidth: QR_SIZE_PX, maxHeight: QR_SIZE_PX }}
                />
                {!tokenInfo && loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                )}
              </Box>
              <Box sx={{ width: '100%', maxWidth: QR_SIZE_PX, mt: 2 }}>
                {tokenInfo && tokenInfo.refreshIntervalSeconds > 0 ? (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={progressPct}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.secondary' }}
                    >
                      Refreshes in {secondsLeft}s
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', textAlign: 'center', color: 'text.secondary' }}
                  >
                    {tokenInfo ? 'QR does not auto-refresh' : 'Waiting for QR…'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right Column: Instructions */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: { xs: 2, md: 4 },
                height: { xs: 'auto', md: QR_SIZE_PX },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'text.primary', mb: 4 }}>
                Instructions for Attendees:
              </Typography>
              <Stack spacing={3.5} component="ol" sx={{ pl: 3.5, m: 0 }}>
                {locationRequired && (
                  <Typography
                    component="li"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '1.35rem',
                      lineHeight: 1.8,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <LocationOnIcon
                      sx={{ fontSize: '1.5rem', mt: 0.35, color: 'text.primary', flexShrink: 0 }}
                    />
                    <Box component="span">
                      Turn on your device <strong>location</strong>.
                    </Box>
                  </Typography>
                )}
                <Typography component="li" sx={{ color: 'text.secondary', fontSize: '1.35rem', lineHeight: 1.8 }}>
                  Open your device <strong>Camera app or QR scanning app</strong>.
                </Typography>
                <Typography component="li" sx={{ color: 'text.secondary', fontSize: '1.35rem', lineHeight: 1.8 }}>
                  Scan the <strong>QR code</strong> on the left and open the link in a browser.
                </Typography>
                <Typography component="li" sx={{ color: 'text.secondary', fontSize: '1.35rem', lineHeight: 1.8 }}>
                  <strong>Log in</strong> with your HIP account
                  {locationRequired ? (
                    <>
                      {' '}
                      and allow <strong>location access</strong> if prompted
                    </>
                  ) : null}
                  .
                </Typography>
                <Typography component="li" sx={{ color: 'text.secondary', fontSize: '1.35rem', lineHeight: 1.8 }}>
                  You are all set once the <strong>success message</strong> is shown!
                </Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventAttendanceQrDialog;
